import { BankModel, BankDataArray, BankQueryParams } from '@app/interfaces/bank';
import { axiosApi } from './axios.api';
import { apiErrorMessage } from './ApiError';

const url = `/api/banks`;

export const getBanksApi = async (params: BankQueryParams): Promise<BankDataArray | undefined> => {
  try {
    const response = await axiosApi.get<BankDataArray>(url, { params });
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const createBankApi = async (formData: BankModel) => {
  try {
    const response = await axiosApi.post(url, formData);
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const updateBankApi = async (bankId: number, formData: BankModel) => {
  try {
    const response = await axiosApi.put(`${url}/${bankId}`, formData);
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const deleteBankApi = async (bankId: number) => {
  try {
    const response = await axiosApi.delete(`${url}/${bankId}`);
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const getVietQRBankList = async () => {
  try {
    const response = await axiosApi.get("https://api.vietqr.io/v2/banks");
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

/** Chỉ cần các trường dựng VietQR — nhận cả BankModel lẫn BankLookupItem. */
type QrBank = Partial<Pick<BankModel, 'bin' | 'account_number' | 'holder_name'>>;

/** .svg chứ không .png: .png của VietQR chuyển hướng sang .svg mà không có header CORS nên trình duyệt chặn. */
export const buildVietQrUrl = (data: QrBank, amount: number, description: string): string =>
  `https://img.vietqr.io/image/${data.bin}-${data.account_number}-compact2.svg?amount=${encodeURIComponent(
    amount.toString(),
  )}&addInfo=${encodeURIComponent(description)}&accountName=${encodeURIComponent(data.holder_name as string)}`;

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/**
 * Ảnh QR dạng data URL để in không phụ thuộc mạng. Không ném lỗi: QR không được làm hỏng màn hình đơn hàng,
 * tải lỗi thì trả URL trực tiếp cho thẻ img. Dùng fetch thay axiosApi để không gửi token đăng nhập cho VietQR
 * và lỗi 5xx của VietQR không đẩy người dùng sang /server-error.
 */
export const getBankShopQRCode = async (
  data: QrBank | undefined,
  amount: number,
  description: string,
): Promise<string | null> => {
  if (!data?.bin || !data?.account_number || !data?.holder_name) return null;

  const urlQR = buildVietQrUrl(data, amount, description);
  try {
    const response = await fetch(urlQR);
    if (!response.ok) return urlQR;
    return await blobToDataUrl(await response.blob());
  } catch {
    return urlQR;
  }
};

export interface BankLookupItem {
  id: number;
  name: string;
  account_number: string;
  holder_name: string;
  /** Mã BIN ngân hàng — cần để dựng VietQR. */
  bin?: BankModel['bin'];
  /** Tài khoản chính của shop. */
  primary?: boolean;
}

// Shop bank accounts for dropdowns — any authenticated user, no permission required.
export const getBankLookupApi = async (): Promise<BankLookupItem[]> => {
  try {
    const response = await axiosApi.get<{ rows: BankLookupItem[] }>(`${url}/lookup`);
    return response.data?.rows ?? [];
  } catch (error: any) {
    apiErrorMessage(error);
    return [];
  }
};

export const exportBankApi = async (params?: string) => {
  try {
    const response = await axiosApi.get(`${url}/export`, { params, responseType: 'blob' });

    const newUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = newUrl;
    link.setAttribute('download', `banks.xlsx`);
    document.body.appendChild(link);
    link.click();
  } catch (error: any) {
    apiErrorMessage(error);
  }
};